import {TZDate} from '@date-fns/tz'
import {format} from 'date-fns'

import {BUSINESS_TIMEZONE} from 'src/api/business-time'
import {apiFetch} from 'src/api/fetch'
import {toApiDateString} from 'src/jobs/job-schedule-format'

import {
    type DeployProjectEquipmentInput,
    deployProjectEquipmentInputSchema,
    equipmentDeploymentResponseSchema,
    type IdentifiedEquipmentReturnInput,
    identifiedEquipmentReturnResponseSchema,
    type ProjectDeployedEquipment,
    projectDeployedEquipmentSchema,
    type StockEquipmentReturnInput,
    stockEquipmentReturnResponseSchema,
    type ThirdPartyEquipmentReturnInput,
} from './schemas'

function serializeEventDate(value: Date | undefined, timeValue?: string | null) {
    if (!value) {
        return undefined
    }
    if (!timeValue) {
        return toApiDateString(value)
    }
    const timeParts = timeValue.split(':')
    if (timeParts.length !== 2) {
        return toApiDateString(value)
    }
    const hours = Number(timeParts[0]!)
    const minutes = Number(timeParts[1]!)
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
        return toApiDateString(value)
    }
    const timestamp = new TZDate(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        hours,
        minutes,
        BUSINESS_TIMEZONE,
    )
    return format(timestamp, "yyyy-MM-dd'T'HH:mm:ssXXX")
}

function serializeReturnedOn(returnedOn: Date | undefined, returnedTime?: string | null) {
    return serializeEventDate(returnedOn, returnedTime)
}

function serializeDeployInput(input: DeployProjectEquipmentInput) {
    const parsed = deployProjectEquipmentInputSchema.parse(input)
    return {
        job_id: parsed.jobId,
        location_id: parsed.locationId,
        deployed_on: serializeEventDate(parsed.deployedOn, parsed.deployedTime),
        rental_asset_id: parsed.rentalAssetId,
        rental_asset_type_id: parsed.rentalAssetTypeId,
        quantity: parsed.quantity,
        third_party_rental_asset_offering_id: parsed.thirdPartyRentalAssetOfferingId,
    }
}

export async function getProjectDeployedEquipment(
    projectId: string,
): Promise<ProjectDeployedEquipment> {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/deployed-equipment`)
    return projectDeployedEquipmentSchema.parse(raw)
}

export async function returnProjectStockEquipment(
    projectId: string,
    input: StockEquipmentReturnInput,
) {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/stock-equipment-returns`, {
        method: 'POST',
        body: JSON.stringify({
            rental_asset_type_id: input.rentalAssetTypeId,
            third_party_rental_asset_offering_id: input.thirdPartyRentalAssetOfferingId,
            quantity: input.quantity,
            yard_id: input.yardId,
            returned_on: serializeReturnedOn(input.returnedOn, input.returnedTime),
        }),
    })
    return stockEquipmentReturnResponseSchema.parse(raw)
}

export async function returnProjectIdentifiedEquipment(
    projectId: string,
    input: IdentifiedEquipmentReturnInput,
) {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/identified-equipment-returns`, {
        method: 'POST',
        body: JSON.stringify({
            rental_asset_ids: input.rentalAssetIds,
            yard_id: input.yardId,
            returned_on: serializeReturnedOn(input.returnedOn, input.returnedTime),
        }),
    })
    return identifiedEquipmentReturnResponseSchema.parse(raw)
}

export async function returnProjectThirdPartyEquipment(
    projectId: string,
    input: ThirdPartyEquipmentReturnInput,
) {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/third-party-equipment-returns`, {
        method: 'POST',
        body: JSON.stringify({
            rental_asset_ids: input.rentalAssetIds,
            yard_id: input.yardId,
            returned_on: serializeReturnedOn(input.returnedOn, input.returnedTime),
        }),
    })
    return identifiedEquipmentReturnResponseSchema.parse(raw)
}

export async function deployProjectEquipment(
    projectId: string,
    input: DeployProjectEquipmentInput,
) {
    const raw = await apiFetch<unknown>(`/projects/${projectId}/equipment-deployments`, {
        method: 'POST',
        body: JSON.stringify(serializeDeployInput(input)),
    })
    return equipmentDeploymentResponseSchema.parse(raw)
}
